import {
  Button,
  ButtonGroup,
  Card,
  CardBody,
  chakra,
  Flex,
  Link,
  Td,
  Text,
  Tr,
} from "@chakra-ui/react";
import { RiFlashlightLine, RiMenuSearchLine } from "@remixicon/react";
import React, { useState } from "react";
import { Link as RouterLink } from "react-router";

import { useDebugEventsData } from "@/app/data/useDebugEventsData";
import CompanyDisplay from "@/common/components/CompanyDisplay";
import { DebugEventTypeDisplay } from "@/common/components/DebugEventTypeSelect";
import EmptyState from "@/common/components/EmptyState";
import { EventCell } from "@/common/components/EventCell";
import InfiniteAnimation from "@/common/components/InfiniteAnimation";
import NotAvailableCell from "@/common/components/NotAvailableCell";
import { SectionHeading } from "@/common/components/SectionHeading";
import SimpleTable from "@/common/components/SimpleTable";
import { SwitchButtonGroup } from "@/common/components/SwitchButtonGroup";
import TimestampCell from "@/common/components/TimestampCell";
import { UserDisplay } from "@/common/components/UserDisplay";
import { useEnvironment } from "@/common/contexts/EnvironmentContext";
import { useSlugParam } from "@/common/hooks/useParam";
import {
  MonitorErrorState,
  MonitorLoadingState,
} from "@/feature/components/EmptyMonitorState";
import { MONITOR_CARD_HEIGHT } from "@/feature/pages/FeatureMonitor";
import { DebugEvent } from "@reflag/shared/eventAPI";
import { DebuggerUrl, GlobalSettingsUrl } from "@reflag/shared/urls";

const EventsIcon = chakra(RiMenuSearchLine, {
  baseStyle: {
    boxSize: 4,
    _dark: {
      color: "gray.500",
    },
    color: "gray.400",
  },
});

export const FeatureEvents = () => {
  const { featureId } = useSlugParam("feature");
  const { env } = useEnvironment();
  const [live, setLive] = useState(true);
  const {
    data: events = [],
    isLoading,
    isError,
    refetch,
  } = useDebugEventsData(
    {
      featureId,
    },
    {
      live,
    },
  );

  // Take only the 5 most recent events
  const recentEvents = events.slice(0, 5);

  return (
    <Flex direction="column" gap={4}>
      <SectionHeading
        fontSize="lg"
        icon={<EventsIcon />}
        rightElement={
          <ButtonGroup>
            <SwitchButtonGroup
              buttons={[
                {
                  leftIcon: live ? (
                    <InfiniteAnimation>
                      <RiFlashlightLine size={12} />
                    </InfiniteAnimation>
                  ) : (
                    <RiFlashlightLine size={12} />
                  ),
                  label: "Live",
                  id: "live",
                },
                {
                  label: "Paused",
                  id: "paused",
                },
              ]}
              value={live ? "live" : "paused"}
              onChange={(value) => setLive(value === "live")}
            />
            <Button
              as={RouterLink}
              to={DebuggerUrl(env, { feature: featureId })}
              variant="outline"
            >
              Explore events
            </Button>
          </ButtonGroup>
        }
      >
        Events
      </SectionHeading>

      <Card>
        <CardBody p={0}>
          {isLoading ? (
            <MonitorLoadingState minH={72} />
          ) : isError ? (
            <MonitorErrorState minH={72} usecase="events" onRetry={refetch} />
          ) : recentEvents.length === 0 ? (
            <EmptyState
              description={
                <Text>
                  Events will show up here as soon as they come in. Note:
                  tracking data is{" "}
                  <Link
                    as={RouterLink}
                    to={GlobalSettingsUrl(env, "app-environments")}
                  >
                    environment
                  </Link>{" "}
                  specific!
                </Text>
              }
              minH={MONITOR_CARD_HEIGHT}
              title="No events the past 3 days"
            />
          ) : (
            <RecentEventsTable data={recentEvents} />
          )}
        </CardBody>
      </Card>
    </Flex>
  );
};

function RecentEventsTable({ data }: { data: DebugEvent[] }) {
  const columns = ["Type", "Event", "Company", "User", "Received"];

  const rowTemplate = (event: DebugEvent, index: number) => (
    <Tr key={index}>
      <Td verticalAlign="top">
        <DebugEventTypeDisplay fontSize="sm" type={event.debugEventType} />
      </Td>
      <Td minW="200px" verticalAlign="top" w="full">
        <EventCell event={event} />
      </Td>
      <Td verticalAlign="top">
        {event.company ? (
          <CompanyDisplay
            company={{
              id: event.company.id,
              name: event.company.name,
              avatarUrl: event.company.avatarUrl,
            }}
            fontSize="sm"
            link
          />
        ) : (
          <NotAvailableCell />
        )}
      </Td>
      <Td verticalAlign="top">
        {event.user ? (
          <UserDisplay
            fontSize="sm"
            link
            user={{
              id: event.user.id,
              name: event.user.name,
              avatarUrl: event.user.avatarUrl,
            }}
          />
        ) : (
          <NotAvailableCell />
        )}
      </Td>
      <Td verticalAlign="top">
        {event.timestamp !== undefined ? (
          <TimestampCell value={event.timestamp} leftAlign />
        ) : (
          <NotAvailableCell />
        )}
      </Td>
    </Tr>
  );

  return (
    <SimpleTable
      colProps={[
        { style: { width: "auto" } },
        { style: { width: "200px" } },
        { style: { width: "auto" } },
        { style: { width: "auto" } },
        { style: { width: "auto" } },
      ]}
      columns={columns}
      minH={MONITOR_CARD_HEIGHT}
      rows={data}
      rowTemplate={rowTemplate}
    />
  );
}
